import { useCallback, useEffect, useRef, useState } from 'react'

// Chrome's native recognizer can silently stop delivering results without
// firing onend. If nothing arrives for this long while the user still wants
// to dictate, the session is recycled (finals are already committed, so
// nothing is lost).
const SILENT_RESTART_MS = 8000

function joinText(prev: string, addition: string) {
  if (!addition) return prev
  if (!prev) return addition
  return /\s$/.test(prev) ? `${prev}${addition}` : `${prev} ${addition}`
}

/**
 * Browser-native speech-to-text (Web Speech API) for dictating a text
 * answer — no server round-trip needed. `onChange` always receives the full
 * updated answer text, so callers can wire it straight to the same setter
 * the textarea's onChange uses. Text streams into the answer while the user
 * is speaking (interim results), and listening keeps going across pauses
 * until the user clicks stop — the browser's recognizer ends its session on
 * every pause, so it is transparently restarted. Falls back to
 * `supported: false` on browsers without SpeechRecognition (e.g. Firefox),
 * so callers should hide the mic button rather than show a broken one.
 */
export default function useDictation(currentText: string, onChange: (v: string) => void) {
  const [listening, setListening] = useState(false)
  const recognitionRef = useRef<any>(null)
  const wantListeningRef = useRef(false)
  const onChangeRef = useRef(onChange)
  // Text that is committed (typed or finalized speech) — interim speech is
  // rendered on top of this but never stored in it.
  const baseRef = useRef(currentText)
  // The exact value this hook last emitted, so a difference from
  // currentText means the user typed/edited while dictating.
  const lastEmittedRef = useRef<string | null>(null)
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => { onChangeRef.current = onChange }, [onChange])
  useEffect(() => {
    if (currentText !== lastEmittedRef.current) {
      // External edit (typing, clearing after submit): adopt it as the base.
      baseRef.current = currentText
      lastEmittedRef.current = null
    }
  }, [currentText])

  const supported = typeof window !== 'undefined' && !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)

  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current)
      watchdogRef.current = null
    }
  }, [])

  const emit = useCallback((value: string) => {
    lastEmittedRef.current = value
    onChangeRef.current(value)
  }, [])

  const startSession = useCallback(() => {
    const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    const recognition = new SpeechRecognitionCtor()
    recognition.lang = 'en-US'
    recognition.continuous = true
    recognition.interimResults = true
    // Finals already folded into baseRef during this session, by result index.
    let committedCount = 0
    let interim = ''

    const armWatchdog = () => {
      clearWatchdog()
      watchdogRef.current = setTimeout(() => {
        if (recognitionRef.current === recognition) recognition.abort()
      }, SILENT_RESTART_MS)
    }

    recognition.onresult = (event: any) => {
      // A cancelled/replaced session must never write into the answer again.
      if (recognitionRef.current !== recognition) return
      armWatchdog()
      interim = ''
      for (let i = committedCount; i < event.results.length; i++) {
        const result = event.results[i]
        const text = String(result[0].transcript || '').trim()
        if (result.isFinal && i === committedCount) {
          baseRef.current = joinText(baseRef.current, text)
          committedCount = i + 1
        } else if (text) {
          interim = joinText(interim, text)
        }
      }
      emit(joinText(baseRef.current, interim))
    }
    recognition.onerror = (event: any) => {
      const code = event && event.error
      if (code === 'not-allowed' || code === 'service-not-allowed' || code === 'audio-capture') {
        wantListeningRef.current = false
      }
    }
    recognition.onend = () => {
      clearWatchdog()
      if (recognitionRef.current !== recognition) return
      recognitionRef.current = null
      // Keep any half-recognised words instead of dropping them on restart.
      if (interim) {
        baseRef.current = joinText(baseRef.current, interim)
        interim = ''
        emit(baseRef.current)
      }
      if (wantListeningRef.current) {
        try {
          startSession()
          return
        } catch {
          wantListeningRef.current = false
        }
      }
      setListening(false)
    }

    recognitionRef.current = recognition
    recognition.start()
    armWatchdog()
  }, [clearWatchdog, emit])

  const stop = useCallback(() => {
    wantListeningRef.current = false
    const recognition = recognitionRef.current
    if (recognition) recognition.stop()
    else setListening(false)
  }, [])

  // Stops immediately and delivers nothing more. Use when the answer being
  // dictated into is going away (moving to another question): stop() is
  // graceful — the browser still delivers trailing results and onend, which
  // by then would go through the NEW question's onChange (e.g. into its code
  // editor). Words heard so far are already in the answer (interim text is
  // emitted as it streams), so nothing visible is lost.
  const cancel = useCallback(() => {
    wantListeningRef.current = false
    clearWatchdog()
    const recognition = recognitionRef.current
    recognitionRef.current = null
    if (recognition) recognition.abort()
    setListening(false)
  }, [clearWatchdog])

  const start = useCallback(() => {
    if (!supported || recognitionRef.current) return
    baseRef.current = currentText
    wantListeningRef.current = true
    try {
      startSession()
      setListening(true)
    } catch {
      wantListeningRef.current = false
      recognitionRef.current = null
      setListening(false)
    }
  }, [supported, currentText, startSession])

  useEffect(() => () => {
    wantListeningRef.current = false
    clearWatchdog()
    const recognition = recognitionRef.current
    recognitionRef.current = null
    if (recognition) recognition.abort()
  }, [clearWatchdog])

  return { supported, listening, start, stop, cancel }
}
